import { createFileRoute, Link } from "@tanstack/react-router";
import { Zap, Webhook, Code2, Plug, MessageCircle, ArrowRight, Sparkles, CheckCircle2, Bot, Wrench, HelpCircle } from "lucide-react";
import { KENTRO } from "@/lib/constants";

const SITE = "https://kentro.com.br";

const GROUPS: { title: string; items: string[] }[] = [
  { title: "Provedores / ISP", items: ["IXCSoft", "Hubsoft", "RadiusNet", "GereNet", "EliteSoft"] },
  { title: "CRM e Marketing", items: ["RD Station", "HubSpot", "SharpSpring", "Pipedrive", "Moskit", "Bitrix24", "Conversion"] },
  { title: "Produtividade e Gestão", items: ["Trello", "ClickUp", "Pipefy", "Kantree", "Google Sheets", "Movidesk"] },
  { title: "E-commerce e Infoprodutos", items: ["Shopify", "Hotmart", "Kiwify", "Braip"] },
  { title: "Pagamentos", items: ["Asaas"] },
  { title: "Comunicação e Outros", items: ["Gmail", "Discord", "Facebook Lead Ads", "Adalo", "1Saas", "Beeminder", "Perseus"] },
  { title: "Inteligência Artificial", items: ["OpenAI / GPT"] },
];

const FAQ = [
  {
    q: "A Kentro integra com meu ERP ou provedor?",
    a: "Sim — temos integrações nativas com os principais ERPs de ISP (IXCSoft, Hubsoft, RadiusNet) e API aberta para conectar qualquer outro sistema.",
  },
  {
    q: "Preciso de programador?",
    a: "Para integrações nativas, não — basta configurar com credenciais. Para integrações personalizadas via API, sim, mas a documentação é clara e o time da Kentro apoia.",
  },
  {
    q: "As integrações são em tempo real?",
    a: "Sim. A Kentro envia eventos via Webhooks no momento em que acontecem e expõe a API REST para leitura e gravação em tempo real.",
  },
  {
    q: "Minhas integrações atuais somem se eu trocar para a Kentro?",
    a: "Não. As nativas continuam funcionando normalmente após a ativação, e as que não são nativas seguem disponíveis via API, Webhooks ou SDK — você não perde nenhuma ferramenta que já usa.",
  },
  {
    q: "Quanto tempo leva pra saber se minha ferramenta é compatível?",
    a: "Se estiver na lista de nativas, a resposta é imediata. Se não estiver, a análise de viabilidade técnica te dá uma resposta objetiva — possível, não possível, ou possível com ajuste — normalmente em poucos dias úteis.",
  },
  {
    q: "Uma integração personalizada trava minha operação até ficar pronta?",
    a: "Não. Seus canais, atendimento e CRM funcionam desde o primeiro dia; a integração personalizada roda em paralelo, sem bloquear o uso da plataforma.",
  },
];

const LEVELS = [
  {
    icon: <CheckCircle2 className="size-5" />,
    title: "Nativas — prontas pra ativar (sem programador)",
    text: "Já vêm conectadas à plataforma. Você ativa pela tela de Configurações, insere a chave de acesso (apikey) e está no ar. Nenhuma linha de código, nenhum desenvolvedor, nenhum prazo de homologação.",
  },
  {
    icon: <Wrench className="size-5" />,
    title: "Personalizadas — via API REST, Webhooks e SDK",
    text: "Seu sistema não está na lista nativa? A Kentro tem API REST documentada (lê e grava contatos, conversas, atendimentos, automações e relatórios), Webhooks em tempo real e SDKs com exemplos de código. Não é “meses de desenvolvimento”: é uma configuração técnica pontual, com documentação e suporte da Kentro, geralmente resolvida pelo seu próprio time de TI ou pelo desenvolvedor do sistema que você já usa.",
  },
  {
    icon: <Bot className="size-5" />,
    title: "Conectores de IA — via MCP (para os assistentes)",
    text: "Quer que o assistente de IA consulte ou execute ações em outro sistema durante a conversa? Isso acontece na aba Conectores/MCP do assistente — o padrão aberto Model Context Protocol amplia o que a IA consegue consultar e executar em tempo real, direto na conversa com o cliente.",
  },
];

const CATEGORY_BENEFITS: { title: string; items: string; benefits: string[] }[] = [
  {
    title: "Provedores de Internet (ISP)",
    items: "IXCSoft, Hubsoft, RadiusNet, GereNet, EliteSoft — prontas",
    benefits: [
      "Consulta automática de boleto, plano e status do contrato do cliente durante o atendimento",
      "Disparo automático de aviso de vencimento e segunda via pelo WhatsApp, direto do sistema de gestão",
      "Abertura e acompanhamento de OS sem sair da conversa",
    ],
  },
  {
    title: "CRM e Marketing",
    items: "RD Station, HubSpot, SharpSpring, Pipedrive, Moskit, Bitrix24, Conversion — prontas",
    benefits: [
      "Sincronização de leads nos dois sentidos: o que entra pelo WhatsApp vira oportunidade no seu funil, e vice-versa",
      "Dados de atendimento e negociação alimentando suas automações de marketing já existentes",
      "Nenhuma base duplicada — o histórico do cliente fica coerente nos dois sistemas",
    ],
  },
  {
    title: "Produtividade e Gestão",
    items: "Trello, ClickUp, Pipefy, Kantree, Google Sheets, Movidesk — prontas",
    benefits: [
      "Criação automática de card/tarefa a partir de um atendimento ou etapa do funil",
      "Exportação de dados de atendimento e relatórios direto pra planilha, sem trabalho manual",
      "Abertura de chamado interno vinculado à conversa original com o cliente",
    ],
  },
  {
    title: "E-commerce e Infoprodutos",
    items: "Shopify, Hotmart, Kiwify, Braip — prontas",
    benefits: [
      "Atendimento pós-venda disparado automaticamente após a confirmação da compra",
      "Recuperação de carrinho/checkout abandonado puxando o lead direto pro WhatsApp",
      "Status do pedido consultável pelo assistente de IA sem abrir outra tela",
    ],
  },
  {
    title: "Pagamentos",
    items: "Asaas — pronta",
    benefits: [
      "Envio de cobrança e segunda via de boleto pelo WhatsApp",
      "Atualização automática de status de pagamento no atendimento e no CRM",
    ],
  },
  {
    title: "Comunicação e Captação de Leads",
    items: "Gmail, Discord, Facebook Lead Ads, Adalo, 1Saas, Beeminder, Perseus — prontas",
    benefits: [
      "Lead de anúncio (Facebook Lead Ads) cai direto na fila certa, sem exportar planilha",
      "Notificação automática da equipe interna (e-mail ou Discord) quando um evento importante acontece na conversa",
    ],
  },
  {
    title: "Inteligência Artificial",
    items: "OpenAI / GPT — pronta",
    benefits: [
      "Potencializa os assistentes de IA da Kentro com modelos de linguagem avançados para respostas, qualificação e execução de procedimentos",
    ],
  },
];

export const Route = createFileRoute("/integracoes")({
  head: () => ({
    meta: [
      { title: "Integrações Kentro: API, Webhooks e +30 Ferramentas" },
      {
        name: "description",
        content:
          "Conecte a Kentro ao seu ERP, CRM e ferramentas favoritas via API REST e Webhooks. Veja todas as integrações: IXCSoft, RD Station, HubSpot, Shopify e mais.",
      },
      { property: "og:title", content: "Integrações Kentro: API, Webhooks e +30 Ferramentas" },
      { property: "og:description", content: "Integre a Kentro ao seu ecossistema via API REST e Webhooks." },
      { property: "og:url", content: `${SITE}/integracoes` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Integrações Kentro" },
      { name: "twitter:description", content: "API REST, Webhooks e +30 integrações nativas." },
      { name: "keywords", content: "integrações Kentro, integração API Kentro, integração webhooks" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/integracoes` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Integrações", item: `${SITE}/integracoes` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: IntegracoesPage,
});

function IntegracoesPage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-16">
        <div className="circuit-pattern absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: "Início", to: "/" }, { label: "Integrações" }]} />
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <Plug className="size-3.5" /> +30 integrações nativas
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            Integrações Kentro: <span className="gradient-text">conecte tudo</span> o que sua operação já usa
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            A Kentro se conecta ao seu ecossistema por <strong className="text-foreground">API REST</strong>,{" "}
            <strong className="text-foreground">Webhooks</strong> e <strong className="text-foreground">SDKs</strong> —
            sem precisar trocar de sistema. Automatize fluxos entre atendimento, vendas, cobrança e marketing, com
            dados sincronizados em tempo real.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          Automatize seu trabalho e construa novos fluxos
        </h2>
        <p className="mt-4 max-w-3xl text-muted-foreground">
          Aumente a produtividade de qualquer equipe. Com a Kentro, qualquer pessoa cria fluxos de trabalho poderosos
          ligando os canais de atendimento aos sistemas que sua empresa já usa — do ERP ao gateway de pagamento.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">Principais integrações disponíveis</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {GROUPS.map((g) => (
            <div key={g.title} className="rounded-2xl border border-border bg-card/40 p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-glow">{g.title}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {g.items.map((it) => (
                  <span
                    key={it}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                    aria-label={`Logo ${it}`}
                  >
                    <Sparkles className="size-3 text-primary-glow" />
                    {it}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="niveis" className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          Sua ferramenta atual já fala com a Kentro? <span className="gradient-text">Veja antes de assinar.</span>
        </h2>
        <p className="mt-4 max-w-3xl text-muted-foreground">
          Toda integração aqui se encaixa em um de três níveis: <strong className="text-foreground">pronta pra ativar hoje</strong>,{" "}
          <strong className="text-foreground">configurável em poucos dias</strong> ou{" "}
          <strong className="text-foreground">conectada direto no seu assistente de IA</strong>. Você não decide isso na
          tentativa — decide lendo a tabela abaixo.
        </p>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          A pergunta mais comum antes de fechar com a Kentro não é "o que a plataforma faz" — é "minha ferramenta atual
          vai continuar funcionando, ou eu preciso contratar um desenvolvedor?". A resposta muda por integração, então em
          vez de deixar isso solto, mostramos exatamente onde cada uma se encaixa.
        </p>

        <h3 className="mt-10 text-xl font-semibold tracking-tight">Os 3 níveis de integração da Kentro</h3>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {LEVELS.map((l) => (
            <Card key={l.title} icon={l.icon} title={l.title}>
              {l.text}
            </Card>
          ))}
        </div>

        <h3 className="mt-12 text-xl font-semibold tracking-tight">O que cada categoria libera na prática</h3>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {CATEGORY_BENEFITS.map((c) => (
            <div key={c.title} className="rounded-2xl border border-border bg-card/40 p-6">
              <h4 className="font-semibold">{c.title}</h4>
              <p className="mt-1 text-xs text-muted-foreground">
                <CheckCircle2 className="mr-1 inline size-3.5 text-emerald-500" />
                {c.items}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                {c.benefits.map((b) => (
                  <li key={b} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <h3 className="text-xl font-semibold tracking-tight">"Minha ferramenta não está na lista. E agora?"</h3>
          <p className="mt-3 text-muted-foreground">
            Não significa que não integra — significa que ainda não é nativa. Nesses casos:
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
              <span>Você usa API REST, Webhooks ou SDK para conectar seu sistema à Kentro, com a documentação técnica completa.</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
              <span>Nossa equipe faz uma análise técnica de viabilidade e te devolve um caminho claro: o que é possível, o que não é, e em quanto tempo sai.</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
              <span>Você acompanha um prazo formalizado por escrito — sem promessa vaga, sem "vamos ver".</span>
            </li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            Ou seja: a ausência da sua ferramenta na lista nativa não é um "não". É só um passo a mais — documentado, com
            prazo e com suporte.
          </p>
        </div>

        <div className="mt-10 overflow-x-auto rounded-2xl border border-border bg-card/40">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-semibold">Nível</th>
                <th className="px-5 py-3 font-semibold">Exige programador?</th>
                <th className="px-5 py-3 font-semibold">Tempo típico</th>
                <th className="px-5 py-3 font-semibold">Exemplos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="px-5 py-4 font-semibold text-foreground">Nativa</td>
                <td className="px-5 py-4 text-muted-foreground">Não</td>
                <td className="px-5 py-4 text-muted-foreground">Ativação imediata</td>
                <td className="px-5 py-4 text-muted-foreground">IXCSoft, RD Station, Shopify, Asaas, Trello</td>
              </tr>
              <tr>
                <td className="px-5 py-4 font-semibold text-foreground">Personalizada (API/Webhook/SDK)</td>
                <td className="px-5 py-4 text-muted-foreground">Não necessariamente — configuração técnica, geralmente pelo seu próprio TI</td>
                <td className="px-5 py-4 text-muted-foreground">Dias, com prazo formalizado</td>
                <td className="px-5 py-4 text-muted-foreground">ERPs, sistemas internos, ferramentas fora da lista</td>
              </tr>
              <tr>
                <td className="px-5 py-4 font-semibold text-foreground">Conector de IA (MCP)</td>
                <td className="px-5 py-4 text-muted-foreground">Não</td>
                <td className="px-5 py-4 text-muted-foreground">Configurado direto no assistente</td>
                <td className="px-5 py-4 text-muted-foreground">Consultas e ações em tempo real durante a conversa</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="gradient-primary shadow-glow mt-10 rounded-2xl p-8 text-white md:p-10">
          <div className="flex items-start gap-3">
            <HelpCircle className="mt-1 size-6 shrink-0" />
            <div>
              <h3 className="text-2xl font-bold md:text-3xl">Não adivinhe se a sua ferramenta encaixa. Pergunte.</h3>
              <p className="mt-2 text-white/85">
                Manda o nome do sistema que você usa hoje — a gente confirma em minutos se é nativo, se precisa de
                configuração, ou como resolve.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/contato" className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90">
                  Falar com um especialista <ArrowRight className="size-4" />
                </Link>
                <a
                  href={KENTRO.whatsappComercialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
                >
                  <MessageCircle className="size-4" /> WhatsApp (38) 3220-8706
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">API REST, Webhooks e SDKs</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          <Card icon={<Code2 className="size-5" />} title="API REST">
            Documentada para ler e gravar dados da sua operação — contatos, conversas, atendimentos, automações e
            relatórios.
          </Card>
          <Card icon={<Webhook className="size-5" />} title="Webhooks">
            Dispare ações automáticas em tempo real assim que um evento acontece — nova mensagem, novo contato,
            mudança de status.
          </Card>
          <Card icon={<Zap className="size-5" />} title="SDKs">
            Documentação clara, exemplos de código e SDKs para acelerar o desenvolvimento das suas integrações
            personalizadas.
          </Card>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border bg-card/40 p-6 md:p-10">
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">Não encontrou sua ferramenta?</h2>
          <p className="mt-3 text-muted-foreground">
            Nossa API aberta permite criar integrações personalizadas. Fale com nosso time e conecte qualquer sistema —
            ERP, gateway, CRM legado ou plataforma proprietária.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/contato" className="gradient-primary shadow-glow inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white hover:opacity-95">
              Falar com um especialista <ArrowRight className="size-4" />
            </Link>
            <Link to="/omnichannel" className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-semibold hover:bg-foreground/5">
              Ver estratégia omnichannel
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">Perguntas frequentes</h2>
        <div className="mt-5 space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card/60">
              <summary className="cursor-pointer list-none font-semibold">
                {f.q}
                <span className="float-right text-muted-foreground group-open:hidden">+</span>
                <span className="float-right text-muted-foreground hidden group-open:inline">−</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Outras dúvidas (planos, LGPD, IA, segmentos):{" "}
          <Link to="/perguntas-frequentes" className="text-primary-glow hover:underline">
            ver todas as perguntas frequentes
          </Link>
          .
        </p>
      </section>

      <FinalCta
        title="Pronto para integrar a Kentro à sua operação?"
        subtitle="Solicite uma demonstração e veja a plataforma rodando com seus próprios sistemas."
      />
    </>
  );
}

export function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      {items.map((it, i) => (
        <span key={it.label} className="inline-flex items-center gap-1">
          {it.to ? (
            <a href={it.to} className="hover:text-foreground">{it.label}</a>
          ) : (
            <span className="text-foreground/80">{it.label}</span>
          )}
          {i < items.length - 1 && <span className="px-1">›</span>}
        </span>
      ))}
    </nav>
  );
}

export function Card({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-6">
      <div className="inline-flex size-10 items-center justify-center rounded-lg bg-primary/15 text-primary-glow">
        {icon}
      </div>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export function FinalCta({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <section className="mx-auto max-w-5xl px-4 pb-20 sm:px-6 lg:px-8">
      <div className="gradient-primary shadow-glow rounded-2xl p-8 text-white md:p-10">
        <h2 className="text-2xl font-bold md:text-3xl">{title}</h2>
        <p className="mt-2 text-white/85">{subtitle}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/contato" className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90">
            Solicitar demonstração
          </Link>
          <a
            href={KENTRO.whatsappComercialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
          >
            <MessageCircle className="size-4" /> WhatsApp (38) 3220-8706
          </a>
        </div>
      </div>
    </section>
  );
}
